import type { PageInfo, PageModel, PtRole, RoleListReq, RolePrivilegeInfo, RolePrivilegeInfoReq } from '@/api/types/pt_entities'
import type { UsableStatus } from '@/api/types/pt_enums'
import type { RequestOptions, ResponseModel } from '@/types/pt_types'
import { apiClient } from '@/axios/http_client'

export interface ApiChangeRoleStatusParam {
  roleId?: number // 角色编号
  status?: UsableStatus // 角色状态
  options?: RequestOptions
}
export interface ApiCreateRoleParam {
  request?: PtRole // 角色信息
  options?: RequestOptions
}
export interface ApiDeleteRoleParam {
  roleId?: number // 角色编号
  options?: RequestOptions
}
export interface ApiDeleteRolesParam {
  request?: RoleListReq // 删除条件
  options?: RequestOptions
}
export interface ApiGetRoleParam {
  roleId?: number // 角色编号
  options?: RequestOptions
}
export interface ApiGetRolePrivilegeIdsParam {
  roleId?: number // 角色编号
  options?: RequestOptions
}
export interface ApiGetRolesParam {
  request?: RoleListReq // 查询条件
  page?: PageInfo // 分页信息
  options?: RequestOptions
}
export interface ApiGetSelfPrivilegesParam {
  type?: string // 上级权限名称
  options?: RequestOptions
}
export interface ApiRefreshRoleParam {
  roleId?: number // 角色编号
  merchantId?: number // 商户编号
  options?: RequestOptions
}
export interface ApiRefreshRolePrivilegeParam {
  roleIds?: string // 角色编号
  options?: RequestOptions
}
export interface ApiSetRolePrivilegesParam {
  request?: RolePrivilegeInfoReq // 查询条件
  options?: RequestOptions
}
export interface ApiUpdateRoleParam {
  roleId?: number // 角色编号
  request?: PtRole // 角色信息
  options?: RequestOptions
}

/**
 * 
 * Created by hongsq.
 */

/**
  * 修改角色状态 - 修改角色状态
  * @param roleId 角色编号
  * @param status 角色状态
  */
export const apiChangeRoleStatus = async (params: ApiChangeRoleStatusParam = {}): Promise<void> => {
  const {  roleId, status, options  } = params
  const resp = await apiClient.formPut<ResponseModel<void>>({
    data: {},
    checkToken: true,
    url: `/api/role/${roleId}/${status}`,
    apiOptions: options
  })
  return resp.data!
}

/**
  * 新增角色 - 新增角色
  * @param request 角色信息
  */
export const apiCreateRole = async (params: ApiCreateRoleParam = {}): Promise<PtRole> => {
  const {  request, options  } = params
  const resp = await apiClient.jsonPost<ResponseModel<PtRole>>({
    data: { ...(request || {}) },
    checkToken: true,
    url: '/api/role',
    apiOptions: options
  })
  return resp.data!
}

/**
  * 删除角色 - 删除角色
  * @param roleId 角色编号
  */
export const apiDeleteRole = async (params: ApiDeleteRoleParam = {}): Promise<void> => {
  const {  roleId, options  } = params
  const resp = await apiClient.delete<ResponseModel<void>>({
    params: {},
    checkToken: true,
    url: `/api/role/${roleId}`,
    apiOptions: options
  })
  return resp.data!
}

/**
  * 删除角色 - 批量删除角色
  * @param request 删除条件
  */
export const apiDeleteRoles = async (params: ApiDeleteRolesParam = {}): Promise<void> => {
  const {  request, options  } = params
  const resp = await apiClient.delete<ResponseModel<void>>({
    params: { ...(request || {}) },
    checkToken: true,
    url: '/api/role',
    apiOptions: options
  })
  return resp.data!
}

/**
  * 查询角色 - 查询角色信息
  * @param roleId 角色编号
  */
export const apiGetRole = async (params: ApiGetRoleParam = {}): Promise<PtRole> => {
  const {  roleId, options  } = params
  const resp = await apiClient.get<ResponseModel<PtRole>>({
    params: {},
    checkToken: true,
    url: `/api/role/${roleId}`,
    apiOptions: options
  })
  return resp.data!
}

/**
  * 查询角色权限列表 - 查询角色权限列表
  * @param roleId 角色编号
  */
export const apiGetRolePrivilegeIds = async (params: ApiGetRolePrivilegeIdsParam = {}): Promise<Array<number>> => {
  const {  roleId, options  } = params
  const resp = await apiClient.get<ResponseModel<Array<number>>>({
    params: {},
    checkToken: true,
    url: `/api/role/privilege/${roleId}`,
    apiOptions: options
  })
  return resp.data!
}

/**
  * 查询角色列表 - 查询一组角色信息
  * @param request 查询条件
  * @param page 分页信息
  */
export const apiGetRoles = async (params: ApiGetRolesParam = {}): Promise<PageModel<PtRole>> => {
  const {  request, page, options  } = params
  const resp = await apiClient.get<ResponseModel<PageModel<PtRole>>>({
    params: { ...(request || {}), ...(page || {}) },
    checkToken: true,
    url: '/api/role/list',
    apiOptions: options
  })
  return resp.data!
}

/**
  * 查询自己的权限列表 - 查询自己的权限列表
  * @param type 上级权限名称
  */
export const apiGetSelfPrivileges = async (params: ApiGetSelfPrivilegesParam = {}): Promise<Array<RolePrivilegeInfo>> => {
  const {  type, options  } = params
  const resp = await apiClient.get<ResponseModel<Array<RolePrivilegeInfo>>>({
    params: { type },
    checkToken: 'MAYBE',
    url: '/api/role/self/privilege',
    apiOptions: options
  })
  return resp.data!
}

/**
  * 刷新角色 - 刷新角色信息
  * @param roleId 角色编号
  * @param merchantId 商户编号
  */
export const apiRefreshRole = async (params: ApiRefreshRoleParam = {}): Promise<void> => {
  const {  roleId, merchantId, options  } = params
  const resp = await apiClient.formPut<ResponseModel<void>>({
    data: { roleId, merchantId },
    checkToken: true,
    url: '/api/role/refresh',
    apiOptions: options
  })
  return resp.data!
}

/**
  * 刷新角色权限 - 刷新角色权限
  * @param roleIds 角色编号
  */
export const apiRefreshRolePrivilege = async (params: ApiRefreshRolePrivilegeParam = {}): Promise<void> => {
  const {  roleIds, options  } = params
  const resp = await apiClient.formPut<ResponseModel<void>>({
    data: { roleIds },
    checkToken: true,
    url: '/api/role/privilege/refresh',
    apiOptions: options
  })
  return resp.data!
}

/**
  * 设置角色权限列表 - 设置角色权限列表
  * @param request 查询条件
  */
export const apiSetRolePrivileges = async (params: ApiSetRolePrivilegesParam = {}): Promise<void> => {
  const {  request, options  } = params
  const resp = await apiClient.formPut<ResponseModel<void>>({
    data: { ...(request || {}) },
    checkToken: true,
    url: '/api/role/privilege',
    apiOptions: options
  })
  return resp.data!
}

/**
  * 修改角色 - 修改角色信息
  * @param roleId 角色编号
  * @param request 角色信息
  */
export const apiUpdateRole = async (params: ApiUpdateRoleParam = {}): Promise<void> => {
  const {  roleId, request, options  } = params
  const resp = await apiClient.jsonPut<ResponseModel<void>>({
    data: { ...(request || {}) },
    checkToken: true,
    url: `/api/role/${roleId}`,
    apiOptions: options
  })
  return resp.data!
}
